// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import StatTileGrid from '../components/StatTileGrid';
import { StatTileData } from '../components/StatTile';
import BarListPanel, { BarRow } from '../components/BarListPanel';
import Section from '../components/Section';
import { useUsers } from '../../shared/users';
import { useProcedures } from '../../shared/procedures';
import { useFlagOn } from '../../shared/featureFlags';
import { toUserTiles, toUserBars, toProcedureTiles, toProcedureBars } from './HomePage.adapters';
import styles from './HomePage.module.css';

// Hardcoded sample numbers, not read from any service. Only rendered behind
// admin-rules-summary-enabled (off by default) so they never reach PROD.
const RULE_TILES: StatTileData[] = [
  { label: 'Rules in effect', value: 14, sub: 'Live alarm → procedure rules' },
  { label: 'Unbound alarms', value: 2, tone: 'warn', sub: 'Fire with no procedure attached' },
  { label: 'Needs attention', value: 6, tone: 'warn', sub: 'Gaps, conflicts and stale rules' },
  { label: 'Coverage', value: 88, unit: '%', tone: 'accent', sub: '14 of 16 alarm paths bound' },
];

const RULE_BARS: BarRow[] = [
  { label: 'PMS', count: 2, pct: 29, tone: 'info' },
  { label: 'FDD', count: 5, pct: 71, tone: 'accent' },
  { label: 'Catch-all', count: 0, pct: 0, tone: 'idle' },
];

/**
 * Owns the `useUsers()` call, not just the rendering it feeds — mounted
 * only when `accessManagementEnabled` is on, so a disabled flag also stops
 * the org roster from being fetched, not just hidden (PR #366 review,
 * Maurice and Federico).
 */
function UsersSection({ navigate }: { navigate: (path: string) => void }) {
  const { stats, roleBreakdown, isLoading, error } = useUsers();
  const userTiles = toUserTiles(stats);
  const userBars = toUserBars(roleBreakdown);

  return (
    <Section
      label="Users"
      openLabel="Open users"
      onOpen={() => navigate('/admin-center/users')}
      loading={isLoading}
      error={error}
    >
      <StatTileGrid tiles={userTiles} />
      <BarListPanel title="By organization role" bars={userBars} />
    </Section>
  );
}

/**
 * Owns the `useProcedures()` call, like UsersSection: mounted only when
 * `adminProceduresSummaryEnabled` is on, so a disabled flag also stops the
 * catalog from being fetched for a summary nobody sees (AB#33812).
 */
function ProceduresSection({ navigate }: { navigate: (path: string) => void }) {
  const {
    stats: procedureStats,
    domainBreakdown,
    isLoading,
    error,
    notice,
    retry,
  } = useProcedures();
  const procedureTiles = toProcedureTiles(procedureStats);
  const procedureBars = toProcedureBars(domainBreakdown);

  return (
    <Section
      label="Procedures"
      openLabel="Open library"
      onOpen={() => navigate('/admin-center/procedures/library')}
      loading={isLoading}
      error={error}
      notice={notice}
      onRetry={retry}
    >
      <StatTileGrid tiles={procedureTiles} />
      <BarListPanel title="By system" bars={procedureBars} />
    </Section>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const accessManagementEnabled = useFlagOn('accessManagementEnabled');
  const adminRulesSummaryEnabled = useFlagOn('adminRulesSummaryEnabled');
  const adminProceduresSummaryEnabled = useFlagOn('adminProceduresSummaryEnabled');

  return (
    <div className={styles.page}>
      <div>
        <h1 className={styles.title}>Home</h1>
        <div className={styles.subtitle}>All facilities</div>
      </div>

      {adminProceduresSummaryEnabled && <ProceduresSection navigate={navigate} />}

      {adminRulesSummaryEnabled && (
        <Section
          label="Rules"
          openLabel="Open rules"
          onOpen={() => navigate('/admin-center/rules')}
        >
          <StatTileGrid tiles={RULE_TILES} />
          <BarListPanel title="Definitions by source" bars={RULE_BARS} />
        </Section>
      )}

      {accessManagementEnabled && <UsersSection navigate={navigate} />}
    </div>
  );
}
